/**
 * SummaryPanel - Summarization interface
 *
 * Uses Gemini AI for document summarization.
 * Supports corpus, per-source, and topic-focused summaries.
 *
 * @module SummaryPanel
 */

import React, { useState, useEffect } from 'react';

import {
  FileText,
  Loader2,
  Copy,
  Check,
  Download,
  RefreshCw,
  Sparkles,
  BookOpen,
  Target,
  Layers,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Cloud,
  HardDrive,
  Info,
} from 'lucide-react';

import ideasKitchenApi from '@/pages/ideas-kitchen/api/ideasKitchenApi';

/**
 * Processing Mode Banner for Summary
 */
const SummaryProcessingBanner = ({ notebook }) => {
  const sources = notebook?.sources || [];
  const localCount = sources.filter(
    s => !s.ingestionProvider || s.ingestionProvider === 'local'
  ).length;
  const googleCount = sources.filter(
    s => s.ocrProvider === 'google_vision' && s.status === 'ready'
  ).length;

  if (sources.length === 0) return null;

  const isLocalOnly = googleCount === 0;

  if (isLocalOnly) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-900/50">
        <HardDrive className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <p className="text-xs text-amber-700 dark:text-amber-300 flex-1">
          <strong>Local Summarization</strong> — Using extracted text. Send to Google for deeper
          analysis.
        </p>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 dark:bg-emerald-950/30 border-b border-emerald-200 dark:border-emerald-900/50">
      <Cloud className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
      <p className="text-xs text-emerald-700 dark:text-emerald-300 flex-1">
        <strong>Google-Powered Summarization</strong> — Deep analysis with {googleCount} indexed
        sources
      </p>
    </div>
  );
};

const SummaryPanel = ({ notebook }) => {
  const [summaryType, setSummaryType] = useState('corpus'); // corpus | source | topic
  const [selectedSource, setSelectedSource] = useState(null);
  const [topicInput, setTopicInput] = useState('');
  const [summaryLength, setSummaryLength] = useState('medium');
  const [summaryFormat, setSummaryFormat] = useState('markdown');

  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [savedOutputs, setSavedOutputs] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [isLoadingOutputs, setIsLoadingOutputs] = useState(false);

  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    if (!notebook?.id) return;
    const loadOutputs = async () => {
      setIsLoadingOutputs(true);
      try {
        const response = await ideasKitchenApi.listOutputs(notebook.id, 'summary');
        if (response.success && Array.isArray(response.outputs)) {
          setSavedOutputs(response.outputs);
        }
      } catch (err) {
        console.error('Failed to load saved outputs:', err);
      } finally {
        setIsLoadingOutputs(false);
      }
    };
    loadOutputs();
  }, [notebook?.id]);

  const handleGenerateSummary = async () => {
    if (!notebook?.id) return;

    setIsLoading(true);
    setError(null);
    setSummary(null);

    try {
      let response;
      const options = { length: summaryLength, format: summaryFormat };

      switch (summaryType) {
        case 'source':
          if (!selectedSource) {
            throw new Error('Please select a source');
          }
          response = await ideasKitchenApi.generateSourceSummary(
            notebook.id,
            selectedSource.id,
            options
          );
          break;
        case 'topic':
          if (!topicInput.trim()) {
            throw new Error('Please enter a topic');
          }
          response = await ideasKitchenApi.generateTopicSummary(
            notebook.id,
            topicInput.trim(),
            options
          );
          break;
        default:
          response = await ideasKitchenApi.generateCorpusSummary(notebook.id, options);
      }

      if (response.success && response.summary) {
        setSummary(response.summary);
      } else {
        throw new Error(response.error || 'Failed to generate summary');
      }
    } catch (err) {
      console.error('Summary failed:', err);
      setError(err.message || 'Failed to generate summary');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!summary?.text) return;
    try {
      await navigator.clipboard.writeText(summary.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleExport = () => {
    if (!summary?.text) return;

    const blob = new Blob([summary.text], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `summary-draft-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSaveSummary = async () => {
    if (!summary?.text || !notebook?.id) return;
    setIsSaving(true);
    setSaveError(null);

    try {
      const payload = {
        type: 'summary',
        content: summary.text,
        sourceIds: Array.isArray(summary.sourcesUsed) ? summary.sourcesUsed : [],
        metadata: {
          summaryType,
          summaryLength,
          summaryFormat,
          topic: summaryType === 'topic' ? topicInput : null,
        },
        citationSnapshot: summary.citations || null,
      };
      const response = await ideasKitchenApi.saveOutput(notebook.id, payload);
      if (response.success && response.output) {
        setSavedOutputs(prev => [response.output, ...prev]);
      } else {
        throw new Error(response.error || 'Failed to save output');
      }
    } catch (err) {
      console.error('Save output failed:', err);
      setSaveError(err.message || 'Failed to save output');
    } finally {
      setIsSaving(false);
    }
  };

  const sources = notebook?.sources || [];

  return (
    <div className="h-full flex flex-col">
      {/* Processing Mode Banner */}
      <SummaryProcessingBanner notebook={notebook} />

      {/* Configuration */}
      <div className="flex-shrink-0 p-4 border-b border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 space-y-4">
        {/* Summary Type Selection */}
        <div>
          <label className="block text-xs font-medium text-slate-500 dark:text-neutral-400 mb-2">
            Summary Type
          </label>
          <div className="flex gap-2">
            {[
              { id: 'corpus', label: 'Entire Corpus', icon: Layers },
              { id: 'source', label: 'Single Source', icon: FileText },
              { id: 'topic', label: 'Topic Focus', icon: Target },
            ].map(type => (
              <button
                key={type.id}
                onClick={() => setSummaryType(type.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  summaryType === type.id
                    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800'
                    : 'bg-slate-100 dark:bg-neutral-800 text-slate-600 dark:text-neutral-400 border border-transparent hover:border-slate-200 dark:hover:border-neutral-700'
                }`}
              >
                <type.icon className="h-4 w-4" />
                {type.label}
              </button>
            ))}
          </div>
        </div>

        {/* Source Selection (for single source) */}
        {summaryType === 'source' && (
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-neutral-400 mb-2">
              Select Source
            </label>
            <select
              value={selectedSource?.id || ''}
              onChange={e => {
                const src = sources.find(s => s.id === e.target.value);
                setSelectedSource(src || null);
              }}
              className="w-full px-3 py-2 bg-slate-100 dark:bg-neutral-800 border-0 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
            >
              <option value="">Choose a source...</option>
              {sources.map(src => (
                <option key={src.id} value={src.id}>
                  {src.displayName}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Topic Input (for topic focus) */}
        {summaryType === 'topic' && (
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-neutral-400 mb-2">
              Focus Topic
            </label>
            <input
              type="text"
              value={topicInput}
              onChange={e => setTopicInput(e.target.value)}
              placeholder="e.g., liability clauses, payment terms..."
              className="w-full px-3 py-2 bg-slate-100 dark:bg-neutral-800 border-0 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
            />
          </div>
        )}

        {/* Advanced Options */}
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-1 text-xs text-slate-500 dark:text-neutral-400 hover:text-slate-700 dark:hover:text-neutral-300"
        >
          {showAdvanced ? (
            <ChevronDown className="h-3 w-3" />
          ) : (
            <ChevronRight className="h-3 w-3" />
          )}
          Advanced options
        </button>

        {showAdvanced && (
          <div className="grid grid-cols-2 gap-4 pt-2">
            <div>
              <label className="block text-xs font-medium text-slate-500 dark:text-neutral-400 mb-1.5">
                Length
              </label>
              <select
                value={summaryLength}
                onChange={e => setSummaryLength(e.target.value)}
                className="w-full px-3 py-2 bg-slate-100 dark:bg-neutral-800 border-0 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
              >
                <option value="short">Short</option>
                <option value="medium">Medium</option>
                <option value="long">Long</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 dark:text-neutral-400 mb-1.5">
                Format
              </label>
              <select
                value={summaryFormat}
                onChange={e => setSummaryFormat(e.target.value)}
                className="w-full px-3 py-2 bg-slate-100 dark:bg-neutral-800 border-0 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
              >
                <option value="markdown">Markdown</option>
                <option value="plaintext">Plain Text</option>
                <option value="bullets">Bullet Points</option>
              </select>
            </div>
          </div>
        )}

        {/* Generate Button */}
        <button
          onClick={handleGenerateSummary}
          disabled={isLoading || !sources.length}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-medium hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="h-5 w-5" />
              Generate Summary
            </>
          )}
        </button>

        {!sources.length && (
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-lg">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <p className="text-xs text-amber-700 dark:text-amber-300">
              Upload some sources first to generate summaries
            </p>
          </div>
        )}
      </div>

      {/* Summary Output */}
      <div className="flex-1 overflow-y-auto p-4">
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 rounded-xl mb-4">
            <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {summary ? (
          <div className="bg-white dark:bg-neutral-800 border border-slate-200 dark:border-neutral-700 rounded-xl overflow-hidden">
            {/* Summary Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-b border-slate-200 dark:border-neutral-700">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                  <Sparkles className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                </div>
                <span className="text-sm font-medium text-slate-700 dark:text-neutral-300">
                  {summaryType === 'corpus' && 'Corpus Summary'}
                  {summaryType === 'source' && `Summary: ${selectedSource?.displayName}`}
                  {summaryType === 'topic' && `Topic: ${topicInput}`}
                </span>
                <span className="px-2 py-0.5 text-[10px] font-medium bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 rounded-full">
                  Draft
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={handleCopy}
                  className="p-2 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded-lg transition-colors"
                  title="Copy"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4 text-slate-500 dark:text-neutral-400" />
                  )}
                </button>
                <button
                  onClick={handleSaveSummary}
                  disabled={isSaving}
                  className="p-2 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded-lg transition-colors disabled:opacity-60"
                  title="Save output"
                >
                  <BookOpen className="h-4 w-4 text-slate-500 dark:text-neutral-400" />
                </button>
                <button
                  onClick={handleExport}
                  className="p-2 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded-lg transition-colors"
                  title="Export as draft"
                >
                  <Download className="h-4 w-4 text-slate-500 dark:text-neutral-400" />
                </button>
              </div>
            </div>

            {/* Summary Content */}
            <div className="p-4">
              <div className="prose prose-sm dark:prose-invert max-w-none">
                {summary?.text ? (
                  summary.text.split('\n').map((line, i) => (
                    <p key={i} className="mb-2 text-slate-700 dark:text-neutral-300">
                      {line}
                    </p>
                  ))
                ) : (
                  <p className="text-slate-500 dark:text-neutral-400 italic">
                    Summary content not available
                  </p>
                )}
              </div>

              {/* Sources used */}
              {summary.sourcesUsed && summary.sourcesUsed.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-100 dark:border-neutral-700">
                  <p className="text-xs text-slate-400 dark:text-neutral-500 mb-2">
                    Sources used:{' '}
                    {Array.isArray(summary.sourcesUsed)
                      ? summary.sourcesUsed.join(', ')
                      : summary.sourcesUsed}
                  </p>
                </div>
              )}
            </div>

            {/* Draft Notice */}
            <div className="px-4 py-2 bg-amber-50 dark:bg-amber-950/30 border-t border-amber-200 dark:border-amber-900/50">
              <p className="text-[10px] text-amber-700 dark:text-amber-300 text-center">
                ⚡ Exploratory draft — verify before using
              </p>
            </div>
          </div>
        ) : !isLoading ? (
          <div className="h-full flex flex-col items-center justify-center px-6 text-center">
            <div className="p-4 bg-slate-100 dark:bg-neutral-800 rounded-2xl mb-4">
              <FileText className="h-10 w-10 text-slate-400 dark:text-neutral-500" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
              Generate a Summary
            </h3>
            <p className="text-sm text-slate-500 dark:text-neutral-400 max-w-md">
              Choose a summary type above and click generate to get AI-powered insights from your
              sources.
            </p>
          </div>
        ) : null}

        {saveError && (
          <div className="mt-4 text-xs text-red-600 dark:text-red-400">
            {saveError}
          </div>
        )}

        {/* Saved outputs */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-semibold text-slate-600 dark:text-neutral-300">
              Saved summaries
            </h4>
            {isLoadingOutputs && (
              <span className="text-[10px] text-slate-400 dark:text-neutral-500">Loading…</span>
            )}
          </div>
          {savedOutputs.length === 0 ? (
            <div className="text-xs text-slate-400 dark:text-neutral-500">
              No saved summaries yet.
            </div>
          ) : (
            <div className="space-y-2">
              {savedOutputs.map(out => (
                <div
                  key={out.id}
                  className="p-3 rounded-lg border border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-800"
                >
                  <div className="text-[10px] text-slate-400 dark:text-neutral-500 mb-1">
                    {out.createdAt ? new Date(out.createdAt).toLocaleString() : 'Saved output'}
                  </div>
                  <div className="text-xs text-slate-700 dark:text-neutral-300 whitespace-pre-wrap">
                    {out.content}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SummaryPanel;
